import {
  useMutation,
  useQueryClient,
  UseMutateFunction,
} from "@tanstack/react-query";

import axios, { AxiosResponse } from "axios";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useTixologi } from "@punchup/data/legacy/hooks";
import { tixologiUrl } from "../../api-base";
import { TICKET_FEE_DEFAULTS_QUERY_KEY } from "./useTicketFeeDefaults";

/** PUT /ticket_types/fee-defaults body. `percentage_fee` in percent (0–100). */
export type UpdateTicketFeeDefaultsParams = {
  fixed_fee: number;
  percentage_fee: number;
};

type UseUpdateTicketFeeDefaultsReturn = {
  mutate: UseMutateFunction<
    AxiosResponse,
    Error,
    UpdateTicketFeeDefaultsParams,
    unknown
  >;
  isPending: boolean;
  isSuccess: boolean;
  error: Error | null;
};

export function useUpdateTicketFeeDefaults({
  venuePageId,
}: {
  venuePageId: string;
}): UseUpdateTicketFeeDefaultsReturn {
  const { tixologiData } = useTixologi({ id: venuePageId, isComedian: false });
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, error } = useMutation({
    mutationFn: async (
      body: UpdateTicketFeeDefaultsParams
    ): Promise<AxiosResponse> =>
      axios.put(tixologiUrl("/ticket_types/fee-defaults"), body, {
        headers: { Authorization: `Bearer ${tixologiData?.token}` },
      }),
    onSuccess: () => {
      toast({ title: "Default ticket fees saved", variant: "success" });
      queryClient.invalidateQueries({
        queryKey: [TICKET_FEE_DEFAULTS_QUERY_KEY, venuePageId],
        refetchType: "active",
      });
    },
    onError: (error) => {
      const serverMessage = axios.isAxiosError(error)
        ? error.response?.data
        : undefined;
      toast({
        title: "Error saving the default ticket fees",
        description:
          (typeof serverMessage === "string" && serverMessage) ||
          error.message ||
          "Something went wrong",
        variant: "destructive",
      });
    },
  });

  return { mutate, isPending, isSuccess, error };
}
